import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { device } from '@/constants/breakpoints';
import { ReactComponent as CloseIcon } from '@/assets/icons/close.icon.svg';
import { Oval } from 'react-loader-spinner';
import { withdrawRequest } from '@/services/financingApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

function WithdrawRequestModal({ showModal, setShowModal, requestId, closeRequestDetails }) {
  const queryClient = useQueryClient();
  const handleWithdrawRequest = useMutation(withdrawRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-finance-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-finance-metrics'] });
      toast.success('Request withdrawn successfully');
      setShowModal(false);
      closeRequestDetails && closeRequestDetails();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  return (
    <AnimatePresence>
      {showModal && (
        <ModalView
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="modal-overlay">
          <motion.div
            initial={{ y: 1000 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.3 }}
            className="modal"
            onClick={(e) => e.stopPropagation()}>
            {/* ======== Content ======== */}
            <ModalContent>
              <ContentView>
                <div className="header">
                  <button type="button" onClick={() => setShowModal(false)}>
                    <CloseIcon />
                  </button>
                </div>
                <div className="content">
                  <div className="question-icon">
                    <span>?</span>
                  </div>
                  <h3>Withdraw request</h3>
                  <p>
                    Are you sure you want to withdraw this request? This action cannot be undone.
                  </p>
                  <button
                    type="button"
                    className="continue"
                    onClick={() => {
                      handleWithdrawRequest.mutate(requestId);
                    }}>
                    {handleWithdrawRequest.isLoading ? (
                      <Oval color="#fff" height={20} width={20} />
                    ) : (
                      'Withdraw request'
                    )}
                  </button>
                </div>
              </ContentView>
            </ModalContent>
          </motion.div>
        </ModalView>
      )}
    </AnimatePresence>
  );
}

export default WithdrawRequestModal;

const ModalView = styled(motion.div)`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9009;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal {
    background-color: ${(props) => props.theme.colors?.white};
    z-index: 30000;
    position: fixed;
    border-radius: 4px;
    width: 450px;
    padding: 30px 0px;
    padding-top: 0;
    padding-bottom: 20px;
    border-radius: 10px;
    max-height: 90%;
    overflow-y: scroll;

    @media ${device.phone} {
      width: 96%;
    }
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  margin: 0;
  justify-content: flex-start;
  flex-direction: column;
  padding: 30px;

  .header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    h3 {
      font-size: 1rem;
      font-weight: 600;
    }
  }
`;

const ContentView = styled.div`
  width: 100%;

  .content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    margin-top: 30px;

    .question-icon {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: ${(props) => props.theme.colors?.secondary};
      color: ${(props) => props.theme.colors?.secondary};
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        font-size: 2rem;
        font-weight: 600;
        color: ${(props) => props.theme.colors?.white};
      }
    }

    .icon {
      path {
        stroke: ${(props) => props.theme.colors?.warning};
      }
    }

    .green-icon {
      path {
        stroke: ${(props) => props.theme.colors?.warning};
      }
    }

    h3 {
      font-size: 1.2rem;
      font-weight: 600;
      margin-bottom: 10px;
      color: ${(props) => props.theme.colors?.secondary};
      margin-top: 30px;
    }

    p {
      font-size: 0.9rem;
      font-weight: 400;
      color: ${(props) => props.theme.colors?.darkGray};
      margin-bottom: 20px;
      text-align: center;
      max-width: 80%;
      line-height: 19px;
    }

    .delete {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 30px;
      background-color: ${(props) => props.theme.colors?.error_200};
      height: 64px;
      border-radius: 16px;
      color: ${(props) => props.theme.colors?.white};
      font-weight: 500;
    }

    .continue {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 30px;
      background-color: ${(props) => props.theme.colors?.primary};
      height: 64px;
      border-radius: 16px;
      color: ${(props) => props.theme.colors?.white};
      font-weight: 500;
    }

    .row {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .secondary {
        width: 48%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 30px;
        background-color: ${(props) => props.theme.colors?.white};
        height: 64px;
        border-radius: 16px;
        color: ${(props) => props.theme.colors?.primary};
        font-weight: 600;
        border: 1px solid ${(props) => props.theme.colors?.primary};
      }

      .primary {
        width: 48%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 30px;
        background-color: ${(props) => props.theme.colors?.primary};
        height: 64px;
        border-radius: 16px;
        color: ${(props) => props.theme.colors?.white};
        font-weight: 600;
      }
    }
  }
`;
